import { useHistory } from "react-router-dom";
import { useLoadedAudioId } from "../../domains/app/contexts/audio.context";
import type { ForYouFeedItem } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { formatArticleDate } from "../../domains/app/utils/renderDate";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../domains/app/utils/getArticleTypeText";
import { usePlaylistPanelContent } from "../../domains/playlist/hooks/usePlaylistPanelContent";
import { useSaveArticle } from "../../domains/user/hooks/useSaveArticle";
import { useShareArticle } from "../../domains/user/hooks/useShareArticle";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventSelectItemArticle } from "../../tools/analytics/domains/article.analytics";
import PlaylistPanel from "../app/PlaylistPanel";
import { useCongressAttribution } from "../../domains/congress/congressAttribution";
import {
  PublicationCard,
  type PublicationCardRecommendation,
} from "../ui/PublicationCard";
import type { SearchArticleItem } from "../../features/search/hooks/useSearchArticlesQuery";
import type {
  ArticleDetail,
  RelatedArticleCard,
} from "../../features/article/hooks/useArticleData";
import type { RoomSectionArticleCard } from "../../domains/room/hooks/useGetRoomSectionsQuery";
import { authorLine } from "../../domains/article/utils/authorLine";

type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

type ArticlePublicationCardData =
  | ArticleDetail
  | RelatedArticleCard
  | SearchArticleItem
  | FeedArticleItem
  | RoomSectionArticleCard;

export default function ArticlePublicationCard({
  roomId,
  article,
  analyticsListName,
  recommendation,
  onClick,
  className,
  showImage,
}: {
  roomId: string | null;
  article: ArticlePublicationCardData;
  analyticsListName: AnalyticsItemList;
  recommendation: PublicationCardRecommendation | null;
  onClick: (() => void) | null;
  className: string | null;
  showImage: boolean;
}) {
  const loadedAudioId = useLoadedAudioId();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { articleLink } = usePageLinks(roomId);
  const { currentUser } = useCurrentUser();
  const playlistPanel = usePlaylistPanelContent();
  const { isSaved, toggleSaveToPlaylists } = useSaveArticle(
    article,
    analyticsListName
  );
  const { triggerShare } = useShareArticle(analyticsListName);
  const congressAttribution = useCongressAttribution(analyticsListName);

  const journal = article.journal;
  const cardImage = article.cardImage;
  const imageUrl =
    showImage === false || cardImage === null
      ? null
      : cardImage.sizes === null
        ? cardImage.url
        : cardImage.sizes.medium.file;
  return (
    <>
      <PublicationCard
        isInPlayer={loadedAudioId === article.id}
        imageUrl={imageUrl}
        description={null}
        title={article.title}
        journalName={journal === null ? null : journal.name}
        author={authorLine(article.primary_author, article.authors)}
        date={formatArticleDate(article.publication_date, activeLang)}
        meta={getArticleTypeText(article.article_type)}
        metaVariant={getArticleTypeVariant(article.article_type)}
        access={article.isOpenAccess === true ? "open" : "closed"}
        recommendation={recommendation}
        onJournalClick={
          journal === null
            ? null
            : () => {
                history.push(`/journal/${journal.uid}`);
              }
        }
        onRecommendationClick={null}
        isNew={false}
        isBookmarked={isSaved}
        onBookmark={
          currentUser === null
            ? null
            : () => {
                void playlistPanel.openArticle(article.slug);
              }
        }
        onShare={
          currentUser === null
            ? null
            : () => {
                void triggerShare(article, currentUser);
              }
        }
        onClick={() => {
          if (onClick !== null) onClick();
          gaEventSelectItemArticle({
            article,
            listName: analyticsListName,
          });
          // Attribute the open to a congress space when this card is
          // drawing one. Gated on `analyticsListName`, NOT on tree position:
          // this component renders its own PlaylistPanel below, whose cards
          // would otherwise inherit the congress from the provider. The
          // destination page reads this back and attaches it to the
          // `view_item` it already fires.
          history.push({
            pathname: articleLink(article),
            state: { congressAttribution },
          });
        }}
        className={className === null ? undefined : className}
      />
      {playlistPanel.state.status === "ready" && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(article, { add, remove });
          }}
        />
      )}
    </>
  );
}
